import { IconButton, IconButtonProps } from '@saucelabs/ds-button';
import { IconThumbsDownFill, IconThumbsUpFill, IconCheckmarkFill } from '@saucelabs/ds-icons';
import classnames from 'classnames';
import React, { forwardRef, useEffect, useState, useRef } from 'react';

import Styles from './DecideButton.styles';
import { usePrevious } from './utils';

export const decideCheckDuration = 1000;

export enum DecideButtonTypes {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT'
}

type DecideProps = {
  isActive: boolean;
  id: string;
  type: DecideButtonTypes;
  onClick: () => void;
  onUnapprove: () => void;
  className?: string;
  size?: 'sm' | 'md';
} & Omit<IconButtonProps, 'type' | 'icon' | 'aria-pressed' | 'className' | 'color' | 'onPress' | 'size' | 'variant'>

type AcceptProps = DecideProps & {
  isNoChanges?: boolean;
  type: DecideButtonTypes.ACCEPT;
}

type RejectProps = DecideProps & {
  isNoChanges?: never;
  type: DecideButtonTypes.REJECT;
}

export type DecideButtonProps = AcceptProps | RejectProps

const BUTTON_TYPES: Record<
DecideButtonTypes,
{ active: string, icon: React.FC<React.SVGProps<SVGSVGElement>> }
> = {
  [DecideButtonTypes.ACCEPT]: { active: 'accepted', icon: IconThumbsUpFill },
  [DecideButtonTypes.REJECT]: { active: 'rejected', icon: IconThumbsDownFill },
};

const DecideButton = forwardRef<
  HTMLButtonElement, DecideButtonProps
>(({ isActive, isNoChanges, onClick, onUnapprove, type, id, className, size = 'md', ...other }, forwardedRef) => {
  const oldIsActive = usePrevious(isActive);
  const oldId = usePrevious(id);
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    if (id !== oldId) {
      setShowAnimation(false);
    } else if (isActive && oldIsActive === false) {
      setShowAnimation(true);
    } else if (showAnimation) {
      setTimeout(() => setShowAnimation(false), decideCheckDuration);
    }
  });

  const Icon = BUTTON_TYPES[type].icon;

  return (
    <IconButton
      ref={forwardedRef}
      {...other}
      aria-pressed={isActive}
      className={classnames('transition-colors duration-300', className, {
        [Styles['check-animation']]: isActive && showAnimation,
        [Styles[BUTTON_TYPES[type].active]]: isActive,
        [Styles['no-changes']]: !isActive && isNoChanges,
        [Styles.default]: !isActive && !isNoChanges,
        [Styles['reject-default']]: type === DecideButtonTypes.REJECT && !isActive,
        [Styles['accept-default']]: type === DecideButtonTypes.ACCEPT && !isActive,
      })}
      color={isActive ? 'primary' : 'secondary'}
      onPress={isActive ? onUnapprove : onClick}
      size={size}
      variant={isActive ? 'contained' : 'outline'}
    >
      <div className="flex relative fill-current">
        <Icon
          className={classnames('w-2 h-2 transition-opacity duration-500', { 'opacity-0': showAnimation, 'opacity-100': !showAnimation })}
        />
        <IconCheckmarkFill className={classnames('absolute m-auto w-2 h-2 transition-opacity duration-500', { 'opacity-0': !showAnimation, 'opacity-100': showAnimation })} />
      </div>
    </IconButton>
  );
});

export default DecideButton;
